import {FileSearchOutlined, HeartOutlined,
  HomeOutlined, MedicineBoxOutlined, WomanOutlined,
} from '@ant-design/icons';
import {
  DatePicker, Divider, Form, Input, InputNumber,
  Radio, Select, Tabs,
} from 'antd';
import PropTypes from 'prop-types';
import React from 'react';

const {TextArea} = Input;

const sectionTitle = (icon, label) => (
  <span className={'flex items-center gap-2'}>
    {icon}
    {label}
  </span>
);

// small helper so every numeric field behaves the same way
const NumField = ({name, label, min = 0, ...rest}) => (
  <Form.Item name={name} label={label}>
    <InputNumber min={min} className={'!w-full'} {...rest} />
  </Form.Item>
);

NumField.propTypes = {
  label: PropTypes.string.isRequired,
  min: PropTypes.number,
  name: PropTypes.array.isRequired,
};

// Single-value "select from list, or type your own" field.
// Stores a plain string in the form (not an array), but renders as a
// tags-mode Select so the user can either pick a preset option or type
// a custom one if it isn't in the dropdown.
const singleValueToArray = value => (value ? [value] : []);
const arrayToSingleValue = value => (Array.isArray(value) ? value[value.length - 1] : value);

const TagSelect = ({name, label, options, placeholder, className, span}) => (
  <Form.Item
    name={name}
    label={label}
    className={className}
    normalize={arrayToSingleValue}
    getValueProps={value => ({value: singleValueToArray(value)})}
  >
    <Select
      mode={'tags'}
      placeholder={placeholder || 'Select or type to add new'}
      options={options.map(o => ({value: o, label: o}))}
      tokenSeparators={[',']}
      {...(span ? {} : {})}
    />
  </Form.Item>
);

TagSelect.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.array.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  placeholder: PropTypes.string,
  span: PropTypes.bool,
};

// ─── Tab 1: Symptoms & Examination ─────────────────────────────────────────
const SymptomsAndExamination = ({form}) => {
  const height = Form.useWatch(['examination', 'height'], form);
  const weight = Form.useWatch(['examination', 'weight'], form);

  React.useEffect(() => {
    if (height && weight) {
      const meters = height / 100;
      const bmi = +(weight / (meters * meters)).toFixed(1);

      if (Number.isFinite(bmi) && bmi > 0) {
        form.setFieldValue(['examination', 'bmi'], bmi);
      }
    }
  }, [height, weight, form]);

  return (
    <div>
      <Form.Item
        name={'symptoms'}
        label={'Symptoms'}
        tooltip={'Type a symptom and press enter to add it'}
      >
        <Select
          mode={'tags'}
          placeholder={'e.g. Fatigue, Bloating, Irregular periods…'}
          tokenSeparators={[',']}
          open={false}
        />
      </Form.Item>

      <Divider orientation={'left'} className={'!text-primary-700 !text-sm'}>Vitals</Divider>

      <div className={'grid grid-cols-2 md:grid-cols-4 gap-x-4'}>
        <Form.Item
          name={['examination', 'pulse']}
          label={'Pulse'}
          rules={[{pattern: /^\d{1,3}$/, message: 'Numbers only, e.g. 78'}]}
        >
          <Input placeholder={'e.g. 78'} />
        </Form.Item>
        <Form.Item
          name={['examination', 'bp']}
          label={'BP'}
          rules={[{pattern: /^\d{2,3}\/\d{2,3}$/, message: 'Format: 120/80'}]}
        >
          <Input placeholder={'e.g. 120/80'} />
        </Form.Item>
        <Form.Item
          name={['examination', 'temperature']}
          label={'Temperature'}
          rules={[{pattern: /^\d{1,3}(\.\d{1,2})?$/, message: 'e.g. 98.6'}]}
        >
          <Input placeholder={'e.g. 98.6'} />
        </Form.Item>
        <Form.Item name={['examination', 'nadi']} label={'Nadi'}>
          <Input />
        </Form.Item>
        <NumField name={['examination', 'height']} label={'Height (cm)'} />
        <NumField name={['examination', 'weight']} label={'Weight (kg)'} />
        <NumField name={['examination', 'bmi']} label={'BMI'} step={0.1} />
      </div>

      <Divider orientation={'left'} className={'!text-primary-700 !text-sm'}>General</Divider>

      <div className={'grid grid-cols-2 md:grid-cols-4 gap-x-4'}>
        <Form.Item name={['examination', 'urine']} label={'Urine'}>
          <Input />
        </Form.Item>
        <Form.Item name={['examination', 'stools']} label={'Stools'}>
          <Input />
        </Form.Item>
        <Form.Item name={['examination', 'hunger']} label={'Hunger'}>
          <Input />
        </Form.Item>
        <Form.Item name={['examination', 'sleep']} label={'Sleep'}>
          <Input />
        </Form.Item>
        <Form.Item name={['examination', 'tongue']} label={'Tongue'} className={'col-span-2'}>
          <Input />
        </Form.Item>
      </div>
    </div>
  );
};

SymptomsAndExamination.propTypes = {form: PropTypes.object.isRequired};

// ─── Tab 2: Menstrual History ──────────────────────────────────────────────
const MenstrualHistorySection = () => (
  <div className={'grid grid-cols-2 md:grid-cols-3 gap-x-4'}>
    <Form.Item name={['menstrualHistory', 'menarche']} label={'Menarche'}>
      <Input placeholder={'Age at first period'} />
    </Form.Item>
    <Form.Item name={['menstrualHistory', 'lmp']} label={'LMP'}>
      <DatePicker className={'!w-full'} format={'YYYY-MM-DD'} />
    </Form.Item>
    <Form.Item name={['menstrualHistory', 'upt']} label={'UPT'}>
      <Radio.Group optionType={'button'} buttonStyle={'solid'}>
        <Radio.Button value={true}>Positive</Radio.Button>
        <Radio.Button value={false}>Negative</Radio.Button>
      </Radio.Group>
    </Form.Item>
    <NumField name={['menstrualHistory', 'cycleLength']} label={'Cycle Length (days)'} />
    <TagSelect
      name={['menstrualHistory', 'flow']}
      label={'Flow'}
      options={['Scanty', 'Moderate', 'Heavy']}
    />
    <TagSelect
      name={['menstrualHistory', 'regularOrIrregular']}
      label={'Regularity'}
      options={['Regular', 'Irregular']}
    />
    <Form.Item name={['menstrualHistory', 'color']} label={'Color'}>
      <Input />
    </Form.Item>
    <TagSelect
      name={['menstrualHistory', 'clots']}
      label={'Clots'}
      options={['Yes', 'No', 'Few']}
    />
    <Form.Item name={['menstrualHistory', 'discharge']} label={'Discharge'}>
      <Input />
    </Form.Item>
    <TagSelect
      name={['menstrualHistory', 'pain']}
      label={'Pain'}
      options={['Yes', 'No', 'Mild', 'Moderate', 'Severe']}
    />
    <Form.Item name={['menstrualHistory', 'bloating']} label={'Bloating'}>
      <Input />
    </Form.Item>
    <Form.Item name={['menstrualHistory', 'breastTenderness']} label={'Breast Tenderness'}>
      <Input />
    </Form.Item>
    <Form.Item
      name={['menstrualHistory', 'moodChanges']}
      label={'Mood Changes'}
      className={'col-span-2 md:col-span-3'}
    >
      <TextArea rows={2} />
    </Form.Item>
  </div>
);

// ─── Tab 3: Obstetric History ──────────────────────────────────────────────
const ObstetricHistorySection = () => (
  <div className={'grid grid-cols-2 md:grid-cols-3 gap-x-4'}>
    <Form.Item name={['obstetricHistory', 'marriedSince']} label={'Married Since'}>
      <DatePicker className={'!w-full'} format={'YYYY-MM-DD'} placeholder={'Select date'} />
    </Form.Item>
    <NumField name={['obstetricHistory', 'previousPregnancy']} label={'Previous Pregnancies'} />
    <NumField name={['obstetricHistory', 'miscarriages']} label={'Miscarriages'} />
    <NumField name={['obstetricHistory', 'ectopicPregnancy']} label={'Ectopic Pregnancies'} />
    <TagSelect
      name={['obstetricHistory', 'ivfOrIuiHistory']}
      label={'IVF / IUI History'}
      options={['None', 'No', 'IUI', 'IVF', 'Ovulation Induction', 'Others']}
      className={'col-span-2'}
    />
    <TagSelect
      name={['obstetricHistory', 'ovulationTracking']}
      label={'Ovulation Tracking'}
      options={['Yes', 'No', 'Ovulation Kit']}
      className={'col-span-2 md:col-span-3'}
    />
  </div>
);

// ─── Tab 4: Lifestyle ───────────────────────────────────────────────────────
const LifestyleSection = () => (
  <div className={'grid grid-cols-2 md:grid-cols-3 gap-x-4'}>
    <TagSelect
      name={['lifestyle', 'agni']}
      label={'Agni'}
      options={['Sama', 'Vishama', 'Tikshna', 'Manda']}
    />
    <TagSelect
      name={['lifestyle', 'diet']}
      label={'Diet'}
      options={['Vegetarian', 'Non-Vegetarian', 'Vegan', 'Eggetarian', 'Mixed']}
    />
    <Form.Item name={['lifestyle', 'exercise']} label={'Exercise'}>
      <Input placeholder={'e.g. Walking, 30 min/day'} />
    </Form.Item>
    <Form.Item name={['lifestyle', 'breakfast']} label={'Breakfast'}>
      <Input />
    </Form.Item>
    <Form.Item name={['lifestyle', 'lunch']} label={'Lunch'}>
      <Input />
    </Form.Item>
    <Form.Item name={['lifestyle', 'evening']} label={'Evening'}>
      <Input />
    </Form.Item>
    <Form.Item name={['lifestyle', 'dinner']} label={'Dinner'}>
      <Input />
    </Form.Item>
    <Form.Item
      name={['lifestyle', 'moodChanges']}
      label={'Mood Changes'}
      className={'col-span-2'}
    >
      <TextArea rows={2} />
    </Form.Item>
  </div>
);

//  Tab 5: Investigation & Diagnosis
const InvestigationAndDiagnosis = () => (
  <div>
    <Form.Item
      name={['investigation', 'investigations']}
      label={'Investigations'}
      tooltip={'Pick from the list or type a test/report name and press enter to add it'}
    >
      <Select
        mode={'tags'}
        placeholder={'e.g. TSH, AMH, Ultrasound…'}
        tokenSeparators={[',']}
        options={[
          'CBC', 'TSH', 'AMH', 'FSH', 'LH', 'Prolactin',
          'Vitamin D', 'Vitamin B12', 'HbA1c', 'USG Pelvis',
          'HSG', 'Semen Analysis', 'Thyroid Profile', 'Pelvic Ultrasound',
        ].map(v => ({value: v, label: v}))}
      />
    </Form.Item>

    <Divider orientation={'left'} className={'!text-primary-700 !text-sm'}>Diagnosis</Divider>

    <Form.Item name={['diagnosis', 'diagnosis']} label={'Diagnosis'}>
      <TextArea rows={2} placeholder={'Clinical diagnosis'} />
    </Form.Item>
    <Form.Item name={['diagnosis', 'doctorRemarks']} label={'Doctor Remarks'}>
      <TextArea rows={3} placeholder={'Additional notes / advice'} />
    </Form.Item>
  </div>
);

const PatientHistoryFormFields = ({form, gender}) => {
  const isFemale = String(gender || '').toUpperCase() === 'FEMALE';

  React.useEffect(() => {
    if (!isFemale) {
      form.setFieldValue('menstrualHistory', undefined);
      form.setFieldValue('obstetricHistory', undefined);
    }
  }, [isFemale, form]);

  const items = [
    {
      key: '1',
      label: sectionTitle(<MedicineBoxOutlined />, 'Symptoms & Exam'),
      children: <SymptomsAndExamination form={form} />,
      forceRender: true,
    },
    ...(isFemale ? [
      {
        key: '2',
        label: sectionTitle(<WomanOutlined />, 'Menstrual'),
        children: <MenstrualHistorySection />,
        forceRender: true,
      },
      {
        key: '3',
        label: sectionTitle(<HeartOutlined />, 'Obstetric'),
        children: <ObstetricHistorySection />,
        forceRender: true,
      },
    ] : []),
    {
      key: '4',
      label: sectionTitle(<HomeOutlined />, 'Lifestyle'),
      children: <LifestyleSection />,
      forceRender: true,
    },
    {
      key: '5',
      label: sectionTitle(<FileSearchOutlined />, 'Investigation & Diagnosis'),
      children: <InvestigationAndDiagnosis />,
      forceRender: true,
    },
  ];

  return <Tabs defaultActiveKey={'1'} className={'ph-form-tabs'} items={items} />;
};

PatientHistoryFormFields.propTypes = {
  form: PropTypes.object.isRequired,
  gender: PropTypes.string,
};

export default PatientHistoryFormFields;
